import LocalStorage from "./localStorage";
import moment from 'moment';
import {
    COUNTRY_ID_CANADA,
    COUNTRY_ID_USA,
    DEFAULT_DATABASE_DATETIME_FORMAT,
    DEFAULT_LIMIT,
    DEFAULT_OFFSET,
    DEFAULT_QUERY,
    DEFAULT_SORT,
    INVOICE_STATUS_OPEN,
    INVOICE_STATUS_PAID,
    INVOICE_STATUS_SENT,
    TABLE_OPTIONS_SUF
} from "./util-constants";
import {getUserTimeFormat, isMilitaryTimeSet, toFrontDate, toLocalTimeFromUTC} from "../common/util/util-dates";
import {FieldCheckbox, FieldDropdownSelect, FieldSelectSearch, FieldText, FieldTextSearch} from "../components/fields";
import React from "react";
import FieldContainer from "../components/fields/field-container";
import {classNames, getLookup} from "../common/util/util-helpers";
import {cloneDeep} from "../common/util/util-vanilla";
import {integerWithCommas, numberWithCommas} from "./util-formaters";
import {BarsArrowDownIcon, BarsArrowUpIcon} from "@heroicons/react/24/solid";
import axios from "axios";
import Env, {_ENV_MODE} from "./env";
import {processResponse} from "../data/services/api-util";
import {Field, FieldsManager} from "../data/services/fields";
import FieldDate from "../components/fields/field-date";
import FieldTextarea from "../components/fields/field-text-area";
import FieldMoney from "../components/fields/field-money";
import FieldCreatableAsync from "../components/fields/field-creatable-async";
import jwt_decode from "jwt-decode";
import Cookie from "./cookie";
import FieldCreatableSelect from "../components/fields/field-creatable-select";
import Badge from "../components/badge";
import Tippy from "@tippyjs/react";
import printJS from "print-js";
import {getColumns, mergeColumns} from "../common/components/resource-table/table-utils";
import FieldTimeCustom from "../common/components/fields/field-time-custom";
import FieldMultiSelect from "../common/components/fields/field-multi-select";
import FieldTextMask from "../common/components/fields/field-text-mask";
import FieldSwitch from "../common/components/fields/field-switch";
import slateHTMLToValue from "../common/components/fields/rich-text-editor/slate-serialize";
import RichTextEditor from "../common/components/fields/rich-text-editor";

export function checkPerm(key, check) {
    const perm = parseInt(getProp(LocalStorage.get('user'), 'permissions.' + key, 0));
    return (perm & check) !== 0;
}

export function getJWT() {
    return ["access_token", "expires_in", "refresh_token", "scope", "token_type"]
        .reduce((memo, it) => {
            memo[it] = Cookie.getCookie(it + "_" + _ENV_MODE);

            return memo;
        }, {});
}

export function openInNewTab(url) {
    window.open(url, '_blank', 'noopener, noreferrer');
}

export function getDecodedJWTJson() {
    let token = getJWT().access_token;
    return token ? jwt_decode(token) : {}
}

export function saveJWTToken(token) {
    for (const [key, value] of Object.entries(token)) {
        Cookie.setCookie(
            key,
            value,
            token?.expires_in
        );
    }
}

export function clearCookies() {
    ["access_token", "expires_in", "refresh_token", "scope", "token_type"].forEach(it => {
        Cookie.setCookie(
            it,
            "",
            -1
        );
    })
}


export function timePickerValueToServerTime(time) {
    if (!time) return "00:00:00";

    return moment(time, getUserTimeFormat()).format("HH:mm:ss");
}

export function timeZoneToUTC(date) {
    let timezone = getUser("Contact.Timezone");

    if (!date || !timezone || !moment(date, DEFAULT_DATABASE_DATETIME_FORMAT).isValid()) return undefined;


    const m = moment.tz(date, DEFAULT_DATABASE_DATETIME_FORMAT, timezone);

    m.utc();

    return m.format(DEFAULT_DATABASE_DATETIME_FORMAT);
}

export function userDateFormatToDateTime(date) {
    const userTimeFormat = getUser("Contact?.DateTimeFormat");

    const dateTime = moment(date, userTimeFormat);

    if (dateTime.isValid()) {
        return dateTime.format(DEFAULT_DATABASE_DATETIME_FORMAT);
    }

    return undefined;
}

export function getProp(object, keys, defaultVal) {
    if (object === undefined || object === null) {
        return defaultVal;
    }
    keys = Array.isArray(keys) ? keys : keys.split('.');
    object = object[keys[0]];
    if (object && keys.length > 1) {
        return getProp(object, keys.slice(1), defaultVal);
    }
    return (object === undefined || object === null) ? defaultVal : object;
}

export function getCurrentTimeSeconds() {
    const d = new Date();
    return Math.round(d.getTime() / 1000);
}

export function numberWithCommasInputChange(n) {
    if (n) {
        let original = n.toString().split(".");
        if (original[1] && original[1].length > 1) {
            return original[0] + (original[1] ? "." + original[1].charAt(0) + original[1].charAt(1) : "");
        } else {
            n = n.replace(/[^0-9.]/g, '');
            n = n.replace(/,/g, "");
            n = n.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
            return n;
        }
    }
}

export function formatMoney(amount, decimalCount = 2, decimal = ".", thousands = ",") {
    try {
        decimalCount = Math.abs(decimalCount);
        decimalCount = isNaN(decimalCount) ? 2 : decimalCount;

        const negativeSign = amount < 0 ? "-" : "";

        let i = parseInt(amount = Math.abs(Number(amount) || 0).toFixed(decimalCount)).toString();
        let j = (i.length > 3) ? i.length % 3 : 0;

        return negativeSign + (j ? i.substr(0, j) + thousands : '') + i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + thousands) + (decimalCount ? decimal + Math.abs(amount - i).toFixed(decimalCount).slice(2) : "");
    } catch (e) {
        console.log(e)
    }
}

export function fieldsToHtml(fieldsCpy, translate, handleInputChange, selects = {}, labelType, addContainerClass) {
    return fieldsCpy
        .filter(
            it => !it?.metadata?.hideDialog && it.type !== 'hidden'
        )
        .reduce((memo, item) => {
            let field;
            if (!item.metadata) {
                item.metadata = {}
            }
            if (!item.props) {
                item.props = {}
            }

            switch (item?.type) {
                case "select":
                    let values;
                    if ((typeof selects[item.name] === "function")) {
                        values = selects[item.name](item, fieldsCpy, handleInputChange);
                    } else if (!!selects[item.name]) {
                        values = selects[item.name];
                    } else if (item?.props?.values) {
                        values = item.props.values;
                    } else {
                        values = getLookup(item.name.replace("ID", ""));
                    }

                    field = (
                        <FieldSelectSearch
                            item={item}
                            values={values}
                            onChange={handleInputChange}
                        />
                    );
                    break;
                case "select-search":
                case "multi-select-search":
                    const api = selects?.[item.name]?.api ?? getProp(item, "props.api", "");
                    const query = selects?.[item.name]?.query
                        ? selects?.[item.name]?.query
                        : getProp(item, "props.query", {});


                    field = (
                        <FieldDropdownSelect
                            item={item}
                            onChange={handleInputChange}
                            defaultOptions={true}
                            loadOptions={
                                (inputValue, callback) => {
                                    axios.get(
                                        Env.getApiUrl(
                                            api,
                                            Object.assign(query, {query: inputValue})),
                                        {
                                            headers: {
                                                'Authorization': 'Bearer ' + getJWT().access_token
                                            }
                                        }
                                    )
                                        .then((response) => {
                                            const result = processResponse(response);

                                            if (result && result.status === 0) {
                                                let list;
                                                if (selects[item.name]?.customizeList) {
                                                    list = selects[item.name].customizeList(result.data.list);
                                                } else {

                                                    list = result.data.list.map((it) => {
                                                        if (typeof item?.props?.searchMap === "function") {
                                                            return item.props.searchMap(it);
                                                        }

                                                        return selects[item.name].searchMap(it);
                                                    });
                                                }

                                                callback(list);
                                            }
                                        })
                                        .catch((error) => {
                                        });
                                }
                            }
                        />
                    );
                    break;
                case "multi-select": {
                    let values;
                    if ((typeof selects[item.name] === "function")) {
                        values = selects[item.name](item, fieldsCpy, handleInputChange);
                    } else if (!!selects[item.name]) {
                        values = selects[item.name];
                    } else if (item?.props?.values) {
                        values = item.props.values;
                    } else {
                        values = getLookup(item.name.replace("ID", ""));
                    }

                    field = (
                        <FieldMultiSelect
                            values={values}
                            addClass="select-css-search form-control p-0"
                            onChange={handleInputChange}
                            {...item}
                            {...item.props}
                        />
                    );
                    break;
                }
                case "creatable-select-search":
                    field = (
                        <FieldCreatableAsync
                            item={item}
                            onChange={handleInputChange}
                            defaultOptions={true}
                            loadOptions={
                                (inputValue, callback) => {
                                    axios.get(
                                        Env.getApiUrl(selects[item.name].api, Object.assign(!!selects[item.name].query ? selects[item.name].query : {}, {query: inputValue})),
                                        {
                                            headers: {
                                                'Authorization': 'Bearer ' + getJWT().access_token
                                            }
                                        }
                                    )
                                        .then((response) => {
                                            const result = processResponse(response);
                                            if (result && result.status === 0) {
                                                const list = result.data.list.map((it) => {
                                                    return selects[item.name].searchMap(it);
                                                });
                                                callback(list);
                                            }
                                        })
                                        .catch((error) => {
                                        });
                                }
                            }
                        />
                    );
                    break;
                case "creatable-select":
                    let creatableValues;
                    if (!!selects[item.name]) {
                        creatableValues = selects[item.name];
                    } else {
                        creatableValues = getLookup(item.name.replace("ID", ""));
                    }

                    field = (
                        <FieldCreatableSelect
                            values={creatableValues}
                            onChange={handleInputChange}
                            item={item}
                        />
                    )
                    break;
                case "rich-text":
                    field = (
                        <RichTextEditor
                            //key={item?.metadata?.innerKey}
                            name={item.name}
                            keywords={item?.metadata?.keywords}
                            onChange={handleInputChange}
                            value={item.value}
                            className={item?.props?.className}
                            {...item.props}
                        />
                    )
                    break;
                case "checkbox":
                    let note;

                    if (item?.metadata?.hideLabel !== false) {
                        item.metadata = !!item?.metadata ? item.metadata : {};
                        item.metadata.hideLabel = true;
                        note = item?.metadata?.note;
                        item.metadata.note = undefined;
                        item.metadata.label = item.metadata.label ?? item.name
                    }

                    field = (
                        <label
                            htmlFor={item?.props?.id ?? item.name}
                            className={classNames(
                                'h-9 flex flex-center items-center justify-start px-2 -ml-2 rounded-lg cursor-pointer text-tm-gray-900',
                                !item.disabled ? "cursor-pointer" : undefined, // add hoverClass metadata
                                !item.disabled && !!item?.metadata?.addHoverClass ? item?.metadata?.addHoverClass : undefined,
                                !item.disabled && !item?.metadata?.addHoverClass ? "hover:bg-tm-gray-50" : undefined
                            )}
                        >
                            <FieldCheckbox
                                item={item}
                                onChange={handleInputChange}
                            />

                            {item?.metadata?.hideLabel !== false && (
                                <div
                                    className="ml-2 flex flex-col text-sm font-semibold select-none"
                                >
                                    {translate("field." + item.metadata.label)}

                                    {!!note && (
                                        <p className="m-0 font-normal text-xs text-tm-gray-700 leading-none">{note}</p>
                                    )}
                                </div>
                            )}
                        </label>
                    );
                    break;
                case "search":
                    field = (
                        <FieldTextSearch
                            item={item}
                            onChange={handleInputChange}
                            translate={translate}
                            type="text"
                        />
                    )
                    break;
                case "date":
                case "datetime":
                case "datetimezone":
                    field = (
                        <FieldDate
                            item={item}
                            onChange={handleInputChange}
                            translate={translate}
                            type="text"
                        />
                    )
                    break;
                case "time-custom" :
                case "timezone-custom":
                    field = (
                        <FieldTimeCustom
                            onChange={handleInputChange}
                            isMilitaryTimeFormat={isMilitaryTimeSet()}
                            addClass={classNames("form-control text-center", item?.props?.addClass)}
                            startWithPM={item?.props?.startWithPM ?? false}
                            {...item}
                        />
                    );
                    break;
                case "money":
                    item.metadata.htmlBefore = () => (
                        <div className="pointer-events-none absolute bottom-[9px] left-0 flex pl-3">
                            <span className="text-gray-500 sm:text-sm">$</span>
                        </div>
                    );

                    item.metadata.htmlAfter = () => (
                        <div className="pointer-events-none absolute bottom-[9px] right-0 flex pr-3">
                            <span className="text-gray-500 sm:text-sm" id="price-currency">USD</span>
                        </div>
                    )

                    item.props.className = "form-control pl-7 pr-12"

                    field = (
                        <FieldMoney
                            item={item}
                            onChange={handleInputChange}
                        />
                    );
                    break;
                case "textarea":
                    field = (
                        <FieldTextarea
                            item={item}
                            onChange={handleInputChange}
                        />
                    );
                    break;
                case "button":
                    field = (
                        <button onClick={() => {
                            handleInputChange(item.name);
                        }}>{item.metadata.render()}</button>
                    );
                    break;
                case "button-group":
                    const buttonData = selects[item.name] ?? item?.metadata?.data;
                    const buttonKeys = Object.keys(buttonData);
                    const addClass = item?.props?.addClass;
                    const addButtonClass = item?.metadata?.addButtonClass;

                    field = (
                        buttonKeys.map((buttonValue, i) => {
                            buttonValue = Number(buttonValue);
                            const isActive = item.value == buttonValue;

                            return (
                                <button
                                    key={buttonData[buttonValue] + "-" + i}
                                    disabled={item.disabled}
                                    className={classNames(
                                        addButtonClass,
                                        isActive
                                            ? "shadow-sm ring-1 ring-black ring-opacity-5 focus-visible:ring-2 focus-visible:ring-link focus-visible:ring-offset-2 focus:outline-none focus-visible:ring-offset-gray-100 focus-visible:ring-opacity-50"
                                            : "text-tm-gray-700 focus-visible:ring-2 focus-visible:ring-link focus-visible:ring-offset-2 focus:outline-none focus-visible:ring-offset-gray-100",
                                        isActive && !item.disabled ? "bg-inverse text-link" : "",
                                        isActive && item.disabled ? "bg-tm-gray-100 text-tm-gray-700" : "",
                                        !!item?.metadata?.buttonText ? item.metadata.buttonText : "text-sm font-medium ",
                                        item.disabled ? "cursor-default" : "",
                                        "p-1.5 lg:px-3 flex justify-center items-center flex-grow-1 rounded-btn"
                                    )}
                                    onClick={() => item.disabled ? null : handleInputChange(item.name, buttonValue)}
                                >
                                    {buttonData[buttonValue]}
                                </button>
                            )
                        })
                    );

                    field = (
                        <div
                            className="flex items-center"
                            key={item.name}
                        >
                            <div
                                className={
                                    classNames(
                                        addClass,
                                        !item.disabled ? "hover:bg-tm-gray-200" : "",
                                        item.disabled ? "opacity-60" : "",
                                        "group p-0.5 flex bg-tm-gray-300 min-h-[2.5rem] rounded-lg",
                                        item?.metadata?.addClass
                                    )
                                }
                            >
                                {field}
                            </div>
                        </div>
                    )
                    break;
                case "mask":
                    field = (
                        <FieldTextMask
                            id={item.name}
                            mask={item.props.mask}
                            showMask
                            className="form-control"
                            onChange={handleInputChange}

                            {...item}
                            {...item.props}
                        />
                    );
                    break;
                default:
                    if (item.type === 'integer') {
                        item.value = item.value.toString().replace(/\D/g, '');
                    }

                    if (item.type === 'float') {
                        item.value = item.value.toString().replace(/[^0-9.]/g, '');

                        if ((item.value.match(/[.]/g) || []).length > 1) {
                            item.value = item.value.toString().replace(".", '');
                        }

                        if (item.value.substr(item.value.indexOf('.')).length > 3) {
                            item.value = item.value.slice(0, -1);
                        }
                    }

                    field = (
                        <FieldText
                            item={item}
                            onChange={handleInputChange}
                        />
                    );
            }

            let fieldWithContainer = (
                <FieldContainer
                    key={item?.prop?.key ?? item.name}
                    item={item}
                    labelType={labelType}
                    addContainerClass={addContainerClass}
                    translate={translate}
                >
                    {field}
                </FieldContainer>
            )

            if (item.type === 'switch') {
                fieldWithContainer = (
                    <div key={item.name} className={item?.metadata?.addContainerClass}>
                        {!!item?.metadata?.htmlBefore && item.metadata.htmlBefore(item)}

                        <FieldSwitch
                            {...item}
                            {...item.props}
                            key={item.name}
                            onChange={handleInputChange}
                        />

                        {!!item?.metadata?.htmlAfter && item.metadata.htmlAfter(item)}
                    </div>
                )
            }

            memo.push(fieldWithContainer);

            return memo;
        }, []);
}

export function getFieldContainerClass(addClass = "", labelType = "") {
    let fieldContainerClass = "";

    switch (labelType) {
        case 'left':
            fieldContainerClass = "relative text-tm-gray-900 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5";
            break;
        default:
            fieldContainerClass = "relative text-tm-gray-700"
    }

    fieldContainerClass = classNames(fieldContainerClass, addClass);

    return fieldContainerClass;
}

export function getFieldLabelClass(metadata, labelType) {
    let formLabelClass;
    if (!!metadata?.formLabelClass) {
        formLabelClass = metadata.formLabelClass;
    } else {
        switch (labelType ?? metadata?.labelType) {
            case 'left':
                formLabelClass = "block text-sm font-semibold text-tm-gray-700 sm:mt-px sm:pt-2";
                break;
            case 'float':
                formLabelClass = "label text-tm-gray-900 font-semibold absolute -top-2.5 left-2.5 label-bg px-1 text-xs z-10"
                break;
            case 'stack':
            default:
                formLabelClass = labelType ?? "flex items-center text-sm font-medium h-5 whitespace-nowrap mb-1";
        }
    }

    return formLabelClass;
}

// TODO: Put this into useQuery hook
export function getQuery(customQuery, location, primaryKey) {
    const key = location;
    let queries = LocalStorage.get(key) ?? {};
    let storedQuery = queries[key] ?? Object.assign(DEFAULT_QUERY, {sortBy: primaryKey});

    return Object.assign(storedQuery, customQuery);
}

export function excludeFields(fields = {}, excludedFields = []) {

    const fieldsUpdate = cloneDeep(fields)

    for (const [key] of Object.entries(fieldsUpdate)) {
        if (!!excludedFields.includes(key)) fieldsUpdate[key].type = "hidden";
    }

    return fieldsUpdate;
}

export const resourceIsLoaded = (resource, prevResource) => {
    return !!resource.data && !resource.isLoading && prevResource.isLoading;
}

export const resourceIsCreated = (resource, prevResource = undefined, key = "id") => {
    if (!!prevResource) { // prevResource is for functional components
        if (!!resource?.create?.[key] && resource.create?.[key] !== prevResource?.create?.[key]) {
            return !!resource?.create?.[key];
        }
    } else {
        return !!resource?.create?.id || !!resource?.create?.ids
    }

    return false;
}

export const resourceIsUpdated = (resource, prevResource) => {
    if (prevResource) { // prevResource is for functional components
        if (!!resource?.update && !prevResource?.update) {
            return !!resource?.update;
        }
    } else {
        return !!resource?.update
    }

    return false;
}

export const resourceIsDeleted = (resource, prevResource) => {
    if (prevResource) { // prevResource is for functional components
        if (prevResource.deleteResource === null && resource.deleteResource === true) {
            return !!resource?.deleteResource;
        }
    } else {
        return !!resource?.deleteResource;
    }

    return false;
}

export function includeFields(fields = {}, includedFields = []) {
    const fieldsUpdate = cloneDeep(fields)

    for (const [key] of Object.entries(fieldsUpdate)) {
        if (!includedFields.includes(key)) fieldsUpdate[key].type = "hidden";
    }

    return fieldsUpdate;
}

export function fillFieldsFromData(fieldTemplates, data) {
    const formats = ['float', 'float_or_empty', 'integer', 'integer_or_empty'];

    if (!data) {
        return fieldTemplates;
    }

    return Object.values(fieldTemplates).reduce((memo, item) => {
        if (item.validate.filter(value => formats.includes(value)).length > 0) {
            const val = data[item.name];
            if (!!val && (val.length > 0) && val.includes(",")) {
                item.value = val;
            } else {
                if (item.validate[0] == 'integer' || item.validate[0] == 'integer_or_empty') {
                    item.value = integerWithCommas(!!val ? val : fieldTemplates[item.name].value);
                } else {
                    item.value = numberWithCommas(!!val ? val : fieldTemplates[item.name].value);
                }
            }
        } else if (item.type === 'select-search' || item.type === 'creatable-select-search') {
            if (!data[item.name]) {
                item.value = null // empty select should be null
            } else {
                item.value = {
                    value: data[item.name],
                    label: !!data[item.name.replace("ID", "")] ? data[item.name.replace("ID", "")] : ""
                }
            }
        } else if (item.type === 'checkbox') {
            item.value = !!data[item.name]
        } else if (item.type === 'rich-text') {
            item.value = data[item.name] ? slateHTMLToValue(data[item.name]) : null;
        } else if (item.type === 'datetimezone') {
            item.value = toLocalTimeFromUTC(data[item.name]);
        } else if (item.type === 'time-custom') {
            item.value = getTimeFromServerDate(data[item.name.replace('Time', '')]);
        } else if (item.type === 'timezone-custom') {
            item.value = getTimeFromServerDate(toLocalTimeFromUTC(data[item.name.replace('Time', '')]));
        } else {
            item.value = data[item.name] ?? fieldTemplates[item.name].value;
        }

        memo[item.name] = item;
        return memo;
    }, {});
}

export function getTimeFromServerDate(date) {
    let dateFormat = "HH:mm:ss";

    if (date?.length > 8) {
        dateFormat = "yyyy-mm-dd HH:mm:ss";
    }

    if (!date || date === 'Invalid date') {
        date = "";
    }

    if (moment(date, dateFormat).format(getUserTimeFormat()) === 'Invalid date') {
        return date;
    }

    return moment(date, dateFormat).format(getUserTimeFormat());
}

export function getDefaultTableOptions(fields, tableCustomDefaults, pagePath, translate) {
    const localStorageKey = pagePath + TABLE_OPTIONS_SUF;
    let options = LocalStorage.get(localStorageKey);

    if (!options) {
        options = getDefaultTableOptionsJSON(fields, tableCustomDefaults, translate);
    } else {
        Object.assign(options, {
            icons: {
                IconSortAsc: BarsArrowUpIcon,
                IconSortDesc: BarsArrowDownIcon,
            }
        });
    }

    if (options?.behaviour?.hasVirtualRows === undefined) {
        options.behaviour.hasVirtualRows = true;
    }

    if (options?.style?.isTableLayoutFixed === undefined) {
        if (!options?.style) {
            options.style = {}
        }

        options.style.isTableLayoutFixed = true;
    }

    return options;
}

export function getDefaultTableOptionsJSON(fields, tableCustomDefaults, translate) {
    let options = {
        behaviour: {
            stickyHeader: true,
            rowSelect: false,
            rowHover: true,
            hasVirtualRows: true
        },
        style: {
            stripedRows: true,
            horizontalLines: false,
            verticalLines: false,
            condensed: false,
            floatingActions: true,
            frozenActionColumn: false,
        },
        classes: {
            tdPadding: "h-12 px-3",
            tdWithSubColumnsPadding: "h-16 pl-3 pr-6",
            tdCondensedWithSubColumnsPadding: "h-12 pl-3 pr-6",

            tdPaddingActions: "align-middle pl-3 pr-6",
            verticalLinesClass: "divide-x divide-tm-gray-300",
            horizontalLinesClass: "border-b border-tm-gray-300",
            maxHeightClass: "max-h-[calc(100vh-15.5rem)]"
        },
        columns: getColumns(fields, translate),
        icons: {
            IconSortAsc: BarsArrowUpIcon,
            IconSortDesc: BarsArrowDownIcon,
        }
    };

    options = Object.keys(options).reduce((memo, option) => {
        if (tableCustomDefaults[option]) {
            if (option === "columns") {
                memo["columns"] = mergeColumns(options, tableCustomDefaults);
            } else {
                memo[option] = Object.assign(options[option], tableCustomDefaults[option]);
            }
        } else {
            memo[option] = options[option];
        }

        return memo;
    }, {})

    return options;
}

export function returnOffsetAndPagination(list, query, paginationPage) {
    const {offset, limit, archived} = query

    return archived ? {
        offset: offset,
        paginationPage: paginationPage
    } : {
        offset: (list.length === 1) ? ((offset - limit) >= 0 ? offset - limit : offset) : offset,
        paginationPage: list.length === 1 ? paginationPage === 1 ? paginationPage : paginationPage - 1 : paginationPage
    }
}

export function getUser(keys, defaultVal) {
    const user = LocalStorage.get('user');

    if (!keys && !defaultVal) {
        return user;
    }

    if (!!keys) {
        return getProp(user, keys, defaultVal);
    }

    return defaultVal;
}

export function updateQuery(queryFields, name, value) {
    let queryClone = cloneDeep(queryFields);

    if ("sortBy" === name) {
        if (value === queryClone.sortBy.value) {
            queryClone.sort.value = queryClone.sort.value === 'ASC' ? 'DESC' : 'ASC'
        }
    }

    if ("ALL_FIELDS" === name) {
        Object.values(queryClone).forEach(field => {
            if (!!field?.metadata?.hasActiveBadge) {
                FieldsManager.updateField(queryClone, field.name, "")
            }
        })
    } else {
        queryClone = FieldsManager.updateField(queryClone, name, value);
    }

    return queryClone;
}


export function groupListBySCAC(list, itemName) {
    let groupedList = {};
    list.sort((a, b) => (a.SCAC > b.SCAC) ? 1 : -1).forEach((it) => {
        if (!groupedList[it.SCAC]) {
            groupedList[it.SCAC] = {
                label: "SCAC: " + it.SCAC,
                options: []
            };
        }

        groupedList[it.SCAC].options.push({
            label: it.SCAC + " " + it[itemName + "Number"],
            value: it[itemName + "ID"],
            metadata: it
        });
    });

    return Object.values(groupedList);
}

export function getFullAddressName(it) {
    const address = it?.AddressName

    return [address, it.CityName, [it.State, it.PostalCode].filter(it => !!it).join(" "), it.CountryName].filter(it => !!it).join(", ")
}

export function getDefaultQueryFields(primaryField, translate) {
    return {
        query: new Field('query', '', [''], false, 'search', {
            hideLabel: true,
            hasActiveBadge: true,
            labelType: "float"
        }, {placeholder: translate("text.search")}),
        sort: new Field('sort', DEFAULT_SORT, [''], false, 'hidden'),
        sortBy: new Field('sortBy', primaryField, [''], false, 'hidden'),
        offset: new Field('offset', DEFAULT_OFFSET, [''], false, 'hidden'),
        limit: new Field('limit', DEFAULT_LIMIT, [''], false, 'select', {
            hideLabel: true,
            labelType: "float"
        }, {
            hasPortal: true,
            placeholder: "100",
            menuPlacement: "top"
        })
    }
}

export function getErrorNotificationWithExceptions(result, action) {

    switch (result.data) {
        case "DRIVER_TRUCK_ALREADY_DISPATCH_ANOTHER_LOAD":
            return ({
                title: "Error updating load",
                content: `Driver/Truck already dispatched on load ${result?.metadata[0]?.LoadNumber}.`,
                messageType: "error",
                actions: {
                    hasDismissAction: true,
                    buttons: [
                        {
                            className: "btn-text text-sm",
                            label: `Open load ${result?.metadata[0]?.LoadNumber}`,
                            onClick: () => openInNewTab("/loads/info/" + result?.metadata[0]?.LoadNumber),
                        }
                    ]
                }
            })
        default:
            return ({
                title: action.data.errorMessage === true ? result.data : action.data.errorMessage,
                messageType: "error"
            })
    }
}

export function translateConstant(constant, translate) {
    return Object.keys(constant).reduce((memo, key) => {
        memo[key] = translate("option." + constant[key]);
        return memo;
    }, {});
}

export function concatFullPhoneNumber(phone) {
    const areaCode = phone.AreaCode ? "(" + phone.AreaCode + ")" : "";
    const phoneExtension = phone.PhoneExtension ? "[" + phone.PhoneExtension + "]" : "";
    return [areaCode, phone.PhoneNumber, phoneExtension].join(" ");
}

export function saveTableColumns(pagePath = "", tableOptions = {}) {
    let tableOptionsUpdate = cloneDeep(tableOptions);

    if (tableOptionsUpdate.icons) {
        delete tableOptionsUpdate.icons;
    }

    LocalStorage.set(pagePath + TABLE_OPTIONS_SUF, tableOptionsUpdate)
}

export const renderInvoiceStatusBadge = (it) => {
    if (!it.InvoiceStatus) {
        switch (it.InvoiceStatusID) {
            case INVOICE_STATUS_OPEN:
                it.InvoiceStatus = 'Open';
                break;
            case INVOICE_STATUS_SENT:
                it.InvoiceStatus = 'Sent';
                break;
            case INVOICE_STATUS_PAID:
                it.InvoiceStatus = 'Paid';
                break;
        }
    }
    if (
        (it.InvoiceStatus !== 'Paid')
        &&
        it.IsRevised
        &&
        !!it?.InvoiceSentDate
        &&
        !!it?.RevisedDate
        &&
        moment(it.InvoiceSentDate, DEFAULT_DATABASE_DATETIME_FORMAT).isBefore(moment(it.RevisedDate, DEFAULT_DATABASE_DATETIME_FORMAT))
    ) {
        return (
            <div className="inline-block space-y-0.5">
                <Badge type="warning"
                       className="block flex-grow-0 text-center px-3 leading-4 rounded-btn text-sm font-medium">
                    Revised
                </Badge>

                <div className="text-red-600 font-bold leading-3">
                    {(it?.RevisedDate ? ' ' + toFrontDate(it.RevisedDate) : '')}
                </div>
            </div>
        )
    } else {
        switch (it.InvoiceStatus) {
            case 'Sent':
                return <div className="inline-block space-y-0.5">
                    {!!it.LastDigitalSendEmail && (
                        <Tippy content={it.LastDigitalSendEmail}>
                            <span className="relative">
                                <Badge type="warning"
                                       className="block flex-grow-0 text-center px-3 leading-4 rounded-btn text-sm font-medium">
                                    {it.InvoiceStatus}
                                </Badge>

                                <div className="text-yellow-600 font-bold leading-3">
                                    {(it?.InvoiceSentDate ? ' ' + toFrontDate(it.InvoiceSentDate) : '')}
                                </div>
                            </span>
                        </Tippy>
                    )}
                    {!it.LastDigitalSendEmail && (
                        <>
                            <Badge type="warning"
                                   className="block flex-grow-0 text-center px-3 leading-4 rounded-btn text-sm font-medium">
                                {it.InvoiceStatus}
                            </Badge>

                            <div className="text-yellow-600 font-bold leading-3">
                                {(it?.InvoiceSentDate ? ' ' + toFrontDate(it.InvoiceSentDate) : '')}
                            </div>
                        </>
                    )}
                </div>
            case 'Paid':
                return <div className="inline-block space-y-0.5">
                    <Badge type="success"
                           className="block flex-grow-0 text-center px-3 leading-4 rounded-btn text-sm font-medium">
                        {it.InvoiceStatus}
                    </Badge>

                    <div className="text-green-600 font-bold leading-3">
                        {(it?.InvoicePaidDate ? ' ' + toFrontDate(it.InvoicePaidDate) : '')}
                    </div>
                </div>
            default:
                return <Badge type="info">{it.InvoiceStatus}</Badge>
        }
    }
}

export function downloadDocument(path, documentName) {
    fetch(path)
        .then(response => response.blob())
        .then(blob => {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', documentName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        })
        .catch(error => {
            console.error('Error downloading PDF:', error);
        });
}

export const onDocumentPrint = (url, type) => {
    switch (type) {
        case 'pdf':
            printJS({
                printable: url,
                type: 'pdf'
            })
            break;
        case 'jpg':
        case 'jpeg':
        case 'gif':
        case 'bmp':
        case 'png':
            printJS({
                printable: url,
                type: 'image'
            })
            break;
        default:
            break;
    }
}

export function getHeaderFilterValues(filters) {
    return filters ? Object.keys(filters)
        .reduce((memo, it) => {
            memo[it] = filters[it].value ? 1 : 0
            return memo
        }, {}) : {}
}

export const getPreFillFromQuery = () => {
    let prefilled = (new URL(document.location)).searchParams;
    prefilled = prefilled.get('prefilled');
    window.history.replaceState({}, document.title, window.location.href.split('?')[0]);

    if (prefilled === "undefined" || !prefilled) {
        return "";
    }

    return prefilled;
}


export const updateContractFields = (fields, name, value) => {
    if (name === 'ContractOnFile') {
        fields.ContractStartDate.disabled = !value
        fields.ContractEndDate.disabled = !value
        fields.ContactNumber.disabled = !value

        fields.ContractStartDate.value = ''
        fields.ContractEndDate.value = ''
        fields.ContactNumber.value = ''

        fields.ContractEndDate.props.minDate = ''
        fields.ContractStartDate.props.maxDate = ''
    }
    if (name === "ContractStartDate") {
        if (!fields.ContractEndDate.props) fields.ContractEndDate.props = {}
        fields.ContractEndDate.props.minDate = value
    }
    if (name === "ContractEndDate") {
        if (!fields.ContractStartDate.props) fields.ContractEndDate.props = {}
        fields.ContractStartDate.props.maxDate = value
    }

    return fields
}

export const updateStateFields = (fields, name, value) => {
    if (name === "CountryID") {
        if ((Number(value) === COUNTRY_ID_USA) || (Number(value) === COUNTRY_ID_CANADA)) {
            fields.StateID.type = 'select'
        } else {
            fields.StateID.type = 'hidden'
            fields.StateID.value = ''
        }
    }

    return fields
}
